import {Helmet} from 'react-helmet-async'
import {absoluteUrl, DEFAULT_OG_IMAGE} from './seoConstants'

interface SeoProps {
  title: string
  description: string
  canonicalPath: string
  ogImage?: string
  ogType?: 'website' | 'article'
  // One or more schema.org JSON-LD objects rendered as <script> tags.
  jsonLd?: readonly object[]
}

// Per-page document head: unique title, description, canonical + Open Graph /
// Twitter tags, and optional JSON-LD structured data. Rendered into <head> by
// react-helmet-async (works client-side and is captured by the build-time
// Puppeteer prerender that snapshots the live DOM).
const Seo = ({title, description, canonicalPath, ogImage, ogType = 'website', jsonLd}: SeoProps) => {
  const canonical = absoluteUrl(canonicalPath)
  const image = absoluteUrl(ogImage ?? DEFAULT_OG_IMAGE)

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Ecranify" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd?.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}

export default Seo
